import { IEventSource } from 'src/eventSource';
import { DataManager } from 'src/kameleoonData';
import { Requester } from 'src/requester';
import { IVisitorCodeManager } from 'src/visitorCodeManager';
import { ClientDataType, IStorage } from 'src/storage';
import { SegmentType } from 'src/targeting';
import { VariableType } from 'src/types';
import { EventManager } from 'src/eventManager';
import { IUrlProvider } from 'src/requester/urlProvider';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';

export type ScheduleType = {
  dateStart: string;
  dateEnd: string;
};

export enum UpdateType {
  Polling,
  RealTime,
}

export type JSONValue =
  | string
  | number
  | boolean
  | { [x: string]: JSONValue }
  | JSONValue[];

export type JSONType = Record<string | number | symbol, JSONValue> | null;

export enum RuleType {
  TARGETED_DELIVERY = 'TARGETED_DELIVERY',
  EXPERIMENTATION = 'EXPERIMENTATION',
}

export enum FeatureFlagSdkLanguageType {
  ANDROID = 'ANDROID',
  SWIFT = 'SWIFT',
  JAVA = 'JAVA',
  CSHARP = 'CSHARP',
  NODEJS = 'NODEJS',
  PHP = 'PHP',
  RUBY = 'RUBY',
  GO = 'GO',
  FLUTTER = 'FLUTTER',
  REACTJS = 'REACTJS',
}

export enum FeatureStatus {
  ACTIVATED = 'ACTIVATED',
  DEACTIVATED = 'DEACTIVATED',
  SCHEDULED = 'SCHEDULED',
}

export enum CustomDataScope {
  Visit = 'VISIT',
  Visitor = 'VISITOR',
  Page = 'Page',
}

export enum ConsentType {
  Required = 'REQUIRED',
  NotRequired = 'NOT_REQUIRED',
}

export enum ConsentBlockingBehaviour {
  PartiallyBlocked = 'PARTIALLY_BLOCK',
  CompletelyBlocked = 'FULLY_BLOCK',
}

export type DeviationType = {
  variationId: string;
  value: number;
};

export type FeatureFlagType = {
  id: number;
  featureKey: string;
  environmentEnabled: boolean;
  variations: FeatureVariationType[];
  defaultVariationKey: string;
  rules: RuleItemType[];
  mutuallyExclusiveGroup?: string | null;
  bucketingCustomDataId?: number | null;
  bucketingCustomDataIndex?: number | null;
};

export type ExperimentType = {
  experimentId: number;
  variationByExposition: VariationByExpositionType[];
};

export type FeatureVariationType = {
  name?: string;
  key: string;
  variables: FeatureVariableType[];
};

export type FeatureVariableType = {
  key: string;
  type: VariableType;
  value: string | number | boolean;
};

export type RuleItemType = {
  type: RuleType;
  id: number;
  respoolTime: number | null;
  order: number;
  exposition: number;
  experimentId: number;
  variationByExposition: VariationByExpositionType[];
  segment: SegmentType | null;
  segmentId?: number | null;
};

export type MappedRuleType = {
  experimentId: number;
  featureId: number;
  // --- Note ---
  // Map<variationKey, variationId>
  variations: Map<string, number>;
};

export type ExperimentInfoType = {
  type: RuleType;
  variations: Map<string, number>;
};

export type VariationByExpositionType = {
  variationKey: string;
  variationId: number;
  exposition: number;
};

export type ConfigurationDataType = {
  featureFlags: FeatureFlagType[];
  configuration: ConfigurationType;
  customData: CustomDataConfigurationType[];
  holdout?: ExperimentType;
  segments?: SegmentType[];
  dateModified?: number;
};

export type ResultConfigurationDataType = {
  configuration?: ConfigurationDataType;
  lastModified?: string;
};

export type ClientConfigurationDataType = Omit<
  ConfigurationDataType,
  'featureFlags' | 'segments'
>;

export type CustomDataConfigurationType = {
  id?: number;
  index: number;
  name?: string;
  localOnly: boolean;
  isMappingIdentifier: boolean;
  scope: CustomDataScope;
};

export type ConfigurationType = {
  realTimeUpdate: boolean;
  consentType: ConsentType;
  dataApiDomain: string;
  consentOptOutBehavior: string;
};

export type ClientConfigurationParametersType = {
  urlProvider: IUrlProvider;
  updateInterval: number;
  storage: IStorage<ClientDataType>;
  dataManager: DataManager;
  requester: Requester;
  eventSource: IEventSource;
  eventManager: EventManager;
  externalVisitorCodeManager: IVisitorCodeManager;
  externalPackageInfo: ExternalPackageInfoType;
  defaultDataFile?: string;
};
